import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import FixedCostForm from './components/FixedCostForm';
import ManualPaymentsForm from './components/ManualPaymentsForm';
import PaymentsManager from './components/PaymentsManager'; // NEU: Import hinzugefügt
import Modal from '../../components/base/Modal';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Button from '../../components/base/Button';

// Interfaces
interface Sale {
  id: string;
  date: string;
  automatId: string;
  automatName: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  profit: number;
}

interface FixedCost {
  id: string;
  automatId?: string;
  automatName?: string;
  name: string;
  category: string;
  costType: string;
  amount?: number;
  interval: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  isRevenue?: boolean;
  revenuePercentage?: number;
  revenueAmount?: number;
  notes?: string;
  status: string;
}

interface ManualPayment {
  id: string;
  date: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
}

interface Investment {
  id: string;
  name: string;
  description: string;
  category: string;
  purchaseDate: string;
  purchasePrice: number;
  usefulLife: number;
  depreciationMethod: 'linear' | 'degressive' | 'sofortabschreibung';
  currentValue: number;
  annualDepreciation: number;
  totalDepreciation: number;
  remainingValue: number;
  supplier: string;
  invoiceNumber: string;
  location: string;
  status: 'aktiv' | 'vollständig-abgeschrieben' | 'verkauft' | 'entsorgt';
  notes: string;
  automatId?: string;
  automatName?: string;
  roi?: number;
  paybackPeriod?: number;
  monthlyRevenue?: number;
}

interface MonthlyData {
  month: string;
  revenue: number;
  costs: number;
  fixedCosts: number;
  manualPayments: number;
  grossProfit: number;
  netProfit: number;
}

interface AutomatonPerformance {
  id: string;
  name: string;
  revenue: number;
  costs: number;
  grossProfit: number;
  fixedCosts: number;
  fixedRevenue: number;
  revenueShare: number;
  netProfit: number;
  netMargin: number;
}

interface Automat {
  id: string;
  name: string;
  location: string;
}

const investmentCategories = [
  'Automaten'
];

const depreciationMethods = [
  { value: 'linear', label: 'Linear' },
  { value: 'degressive', label: 'Degressiv' },
  { value: 'sofortabschreibung', label: 'Sofortabschreibung' }
];

export default function Finanzen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Automatische Monatserkennung - wird jeden Tag aktualisiert
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Automatische Aktualisierung des aktuellen Monats
  useEffect(() => {
    const updateCurrentMonth = () => {
      const now = new Date();
      const newCurrentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      if (newCurrentMonth !== currentMonth) {
        console.log(`🔄 Automatischer Monatswechsel erkannt: ${currentMonth} → ${newCurrentMonth}`);
        setCurrentMonth(newCurrentMonth);
      }
    };

    // Prüfe sofort beim Laden
    updateCurrentMonth();

    // Prüfe jeden Tag um Mitternacht (alle 60 Sekunden für Demo-Zwecke)
    const interval = setInterval(updateCurrentMonth, 60000);

    // Cleanup
    return () => clearInterval(interval);
  }, [currentMonth]);

  // States
  const [sales, setSales] = useState<Sale[]>([]);
  const [fixedCosts, setFixedCosts] = useState<FixedCost[]>([]);
  const [manualPayments, setManualPayments] = useState<ManualPayment[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [isInvestmentModalOpen, setIsInvestmentModalOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData>({
    month: currentMonth,
    revenue: 0,
    costs: 0,
    fixedCosts: 0,
    manualPayments: 0,
    grossProfit: 0,
    netProfit: 0
  });
  const [automatonPerformance, setAutomatonPerformance] = useState<AutomatonPerformance[]>([]);

  // Investment form data
  const [investmentFormData, setInvestmentFormData] = useState({
    name: '',
    description: '',
    category: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: '',
    usefulLife: '5',
    depreciationMethod: 'linear' as const,
    supplier: '',
    invoiceNumber: '',
    location: '',
    automatId: '',
    notes: '',
    monthlyRevenue: ''
  });

  // Additional states for Rückzahlungen (refunds)
  const [rueckzahlungen, setRueckzahlungen] = useState<any[]>([]);
  const [rueckzahlungForm, setRueckzahlungForm] = useState({
    payment_id: '',
    amount: '',
    reason: '',
    notes: ''
  });
  const [isAddingRueckzahlung, setIsAddingRueckzahlung] = useState(false);
  const [isRueckzahlungModalOpen, setIsRueckzahlungModalOpen] = useState(false);
  const [zahlungen, setZahlungen] = useState<any[]>([]); // Missing state declaration added

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      console.log('🔄 LOADING ALL DATA...');

      // Load sales
      const { data: salesData, error: salesError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'sale');

      if (salesError) throw salesError;

      const salesList = salesData.map(item => ({
        id: item.id,
        ...item.data
      }));
      setSales(salesList);
      console.log('📊 Loaded sales:', salesList.length);

      // Load automaten - KORRIGIERT: Nur echte Automaten laden
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) throw automatenError;

      // ERWEITERT: Nur physische Automaten für Finanzen verwenden
      const automatenList = automatenData
        .filter(item => {
          // Filtere nur echte physische Automaten
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name,
          location: item.data.location
        }));
      
      console.log('🤖 Loaded automaten for Finance:', automatenList.length);
      console.log('🤖 Automaten names for Finance:', automatenList.map(a => a.name));
      
      setAutomaten(automatenList);

      // Load investments
      const { data: investmentsData, error: investmentsError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'investment')
        .order('created_at', { ascending: false });

      if (investmentsError) throw investmentsError;

      const investmentsList = investmentsData.map(item => ({
        id: item.id,
        ...item.data
      }));
      setInvestments(investmentsList);
      console.log('💼 Loaded investments:', investmentsList.length);

      // Load fixed costs from fixkosten table
      const { data: fixedCostsData, error: fixedCostsError } = await supabase
        .from('fixkosten')
        .select('*')
        .eq('user_id', user?.id);

      if (fixedCostsError) {
        console.error('❌ Error loading fixed costs:', fixedCostsError);
        throw fixedCostsError;
      }

      console.log('🔍 RAW fixedCosts from database:', fixedCostsData);
      console.log('🔍 Total fixedCosts loaded:', fixedCostsData?.length || 0);

      // Convert and validate each fixed cost
      const fixedCostsList = (fixedCostsData || []).map(item => {
        const cost = {
          id: item.id,
          automatId: item.automat_id,
          automatName: item.automat_name,
          name: item.name || 'Unbenannt',
          category: item.category || 'Sonstige',
          costType: item.cost_type || 'fixkosten',
          amount: typeof item.amount === 'number' ? item.amount : (parseFloat(item.amount) || 0),
          interval: item.interval || 'monatlich',
          startDate: item.start_date,
          endDate: item.end_date,
          description: item.description,
          isRevenue: item.is_revenue === true,
          revenuePercentage: typeof item.revenue_percentage === 'number' ? item.revenue_percentage : (parseFloat(item.revenue_percentage) || 0),
          revenueAmount: typeof item.revenue_amount === 'number' ? item.revenue_amount : (parseFloat(item.revenue_amount) || 0),
          notes: item.notes,
          status: item.status || 'aktiv'
        };

        console.log('🔍 Processed cost:', {
          name: cost.name,
          amount: cost.amount,
          type: typeof cost.amount,
          status: cost.status,
          isRevenue: cost.isRevenue,
          costType: cost.costType
        });

        return cost;
      });

      console.log('✅ FINAL processed fixedCosts:', fixedCostsList.length);
      console.log('✅ Sample processed cost:', fixedCostsList[0]);

      setFixedCosts(fixedCostsList);

      // Load manual payments
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'manual_payment');

      if (paymentsError) throw paymentsError;

      const paymentsList = paymentsData.map(item => ({
        id: item.id,
        ...item.data
      }));
      setManualPayments(paymentsList);
      console.log('💳 Loaded manual payments:', paymentsList.length);
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setLoading(false);
      console.log('✅ Loading complete');
    }
  };

  // Investment calculation functions
  const calculateROI = (investment: Investment): { roi: number; paybackPeriod: number } => {
    const monthlyRevenue = investment.monthlyRevenue || 0;
    const annualRevenue = monthlyRevenue * 12;
    const roi = investment.purchasePrice > 0 ? (annualRevenue / investment.purchasePrice) * 100 : 0;
    const paybackPeriod = monthlyRevenue > 0 ? investment.purchasePrice / monthlyRevenue : 0;

    return { roi, paybackPeriod };
  };

  const calculateDepreciation = (
    investment: Omit<
      Investment,
      'id' | 'currentValue' | 'annualDepreciation' | 'totalDepreciation' | 'remainingValue' | 'roi' | 'paybackPeriod'
    >
  ) => {
    const currentYear = new Date().getFullYear();
    const purchaseYear = new Date(investment.purchaseDate).getFullYear();
    const yearsElapsed = currentYear - purchaseYear;

    let annualDepreciation = 0;
    let totalDepreciation = 0;
    let currentValue = investment.purchasePrice;

    if (investment.depreciationMethod === 'sofortabschreibung') {
      annualDepreciation = investment.purchasePrice;
      totalDepreciation = investment.purchasePrice;
      currentValue = 0;
    } else if (investment.depreciationMethod === 'linear') {
      annualDepreciation = investment.purchasePrice / investment.usefulLife;
      totalDepreciation = Math.min(annualDepreciation * yearsElapsed, investment.purchasePrice);
      currentValue = investment.purchasePrice - totalDepreciation;
    } else if (investment.depreciationMethod === 'degressive') {
      const depreciationRate = 0.2;
      let remainingValue = investment.purchasePrice;

      for (let i = 0; i < yearsElapsed && remainingValue > 0; i++) {
        const yearlyDepreciation = remainingValue * depreciationRate;
        totalDepreciation += yearlyDepreciation;
        remainingValue -= yearlyDepreciation;
      }

      annualDepreciation = remainingValue > 0 ? remainingValue * depreciationRate : 0;
      currentValue = investment.purchasePrice - totalDepreciation;
    }

    const { roi, paybackPeriod } = calculateROI({
      ...investment,
      currentValue,
      annualDepreciation,
      totalDepreciation,
      remainingValue: Math.max(0, currentValue)
    } as Investment);

    return {
      currentValue: Math.max(0, currentValue),
      annualDepreciation,
      totalDepreciation,
      remainingValue: Math.max(0, currentValue),
      roi,
      paybackPeriod
    };
  };

  // Investment form handlers
  const handleInvestmentInputChange = (field: string, value: string | number) => {
    setInvestmentFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetInvestmentForm = () => {
    setInvestmentFormData({
      name: '',
      description: '',
      category: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchasePrice: '',
      usefulLife: '5',
      depreciationMethod: 'linear',
      supplier: '',
      invoiceNumber: '',
      location: '',
      automatId: '',
      notes: '',
      monthlyRevenue: ''
    });
  };

  const handleInvestmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedAutomat = automaten.find(a => a.id === investmentFormData.automatId);
    const baseInvestment = {
      name: investmentFormData.name,
      description: investmentFormData.description,
      category: investmentFormData.category,
      purchaseDate: investmentFormData.purchaseDate,
      purchasePrice: parseFloat(investmentFormData.purchasePrice),
      usefulLife: parseInt(investmentFormData.usefulLife),
      depreciationMethod: investmentFormData.depreciationMethod,
      supplier: investmentFormData.supplier,
      invoiceNumber: investmentFormData.invoiceNumber,
      location: investmentFormData.location,
      automatId: investmentFormData.automatId || undefined,
      automatName: selectedAutomat?.name || undefined,
      status: 'aktiv' as const,
      notes: investmentFormData.notes,
      monthlyRevenue: investmentFormData.monthlyRevenue ? parseFloat(investmentFormData.monthlyRevenue) : undefined
    };

    const calculatedValues = calculateDepreciation(baseInvestment);

    const investmentData = {
      ...baseInvestment,
      ...calculatedValues
    };

    try {
      if (editingInvestment) {
        // Update existing investment
        const { error } = await supabase
          .from('daten')
          .update({
            data: investmentData
          })
          .eq('id', editingInvestment.id);

        if (error) throw error;

        setInvestments(prev =>
          prev.map(inv =>
            inv.id === editingInvestment.id ? { id: editingInvestment.id, ...investmentData } : inv
          )
        );

        setEditingInvestment(null);
      } else {
        // Create new investment
        const { data, error } = await supabase
          .from('daten')
          .insert({
            user_id: user?.id,
            data_type: 'investment',
            data: investmentData
          })
          .select()
          .single();

        if (error) throw error;

        setInvestments(prev => [{ id: data.id, ...investmentData }, ...prev]);
      }

      setIsInvestmentModalOpen(false);
      resetInvestmentForm();
    } catch (error) {
      console.error('Error saving investment:', error);
    }
  };

  const handleEditInvestment = (investment: Investment) => {
    setEditingInvestment(investment);
    setInvestmentFormData({
      name: investment.name,
      description: investment.description,
      category: investment.category,
      purchaseDate: investment.purchaseDate,
      purchasePrice: investment.purchasePrice.toString(),
      usefulLife: investment.usefulLife.toString(),
      depreciationMethod: investment.depreciationMethod,
      supplier: investment.supplier,
      invoiceNumber: investment.invoiceNumber,
      location: investment.location,
      automatId: investment.automatId || '',
      notes: investment.notes,
      monthlyRevenue: investment.monthlyRevenue?.toString() || ''
    });
    setIsInvestmentModalOpen(true);
  };

  const handleDeleteInvestment = async (id: string) => {
    if (!confirm('Investition wirklich löschen?')) return;

    try {
      const { error } = await supabase.from('daten').delete().eq('id', id);

      if (error) throw error;

      setInvestments(prev => prev.filter(investment => investment.id !== id));
    } catch (error) {
      console.error('Error deleting investment:', error);
    }
  };

  // Handle fixed costs save from form
  const handleFixedCostsSave = async (savedFixedCosts: any[]) => {
    console.log('🔄 Received saved fixed costs from form:', savedFixedCosts.length);
    await loadAllData();

    setTimeout(() => {
      setActiveTab('overview');
    }, 500);
  };

  // NEU: Einzelne Fixkosten löschen
  const handleDeleteFixedCost = async (costId: string, costName: string) => {
    if (!confirm(`Möchten Sie die Fixkosten "${costName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting fixed cost:', costId, costName);
      
      const { error } = await supabase
        .from('fixkosten')
        .delete()
        .eq('id', costId)
        .eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error deleting fixed cost:', error);
        throw error;
      }

      console.log('✅ Fixed cost deleted successfully');
      
      // Daten neu laden
      await loadAllData();
      
      alert(`✅ Fixkosten "${costName}" wurde erfolgreich gelöscht!`);
    } catch (error) {
      console.error('❌ Error deleting fixed cost:', error);
      alert(`❌ Fehler beim Löschen der Fixkosten "${costName}". Bitte versuchen Sie es erneut.`);
    }
  };

  // NEU: Einzelne Fixkosten bearbeiten
  const handleEditFixedCost = async (cost: FixedCost) => {
    const newName = prompt('Neuer Name:', cost.name);
    if (!newName || newName.trim() === '') return;

    const newAmountStr = prompt('Neuer Betrag (CHF):', cost.amount?.toString() || '0');
    if (!newAmountStr) return;

    const newAmount = parseFloat(newAmountStr);
    if (isNaN(newAmount)) {
      alert('Bitte geben Sie einen gültigen Betrag ein.');
      return;
    }

    try {
      console.log('✏️ Editing fixed cost:', cost.id, newName, newAmount);
      
      const { error } = await supabase
        .from('fixkosten')
        .update({
          name: newName.trim(),
          amount: newAmount
        })
        .eq('id', cost.id)
        .eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error updating fixed cost:', error);
        throw error;
      }

      console.log('✅ Fixed cost updated successfully');
      
      // Daten neu laden
      await loadAllData();
      
      alert(`✅ Fixkosten "${newName}" wurde erfolgreich aktualisiert!`);
    } catch (error) {
      console.error('❌ Error updating fixed cost:', error);
      alert(`❌ Fehler beim Bearbeiten der Fixkosten. Bitte versuchen Sie es erneut.`);
    }
  };

  // Rückzahlung hinzufügen
  const handleAddRueckzahlung = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rueckzahlungForm.payment_id || !rueckzahlungForm.amount || !rueckzahlungForm.reason || !user) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    // Validierung: Betrag darf nicht höher als ursprüngliche Zahlung sein
    const originalPayment = zahlungen.find(z => z.id === rueckzahlungForm.payment_id);
    if (!originalPayment) {
      alert('Die ausgewählte Zahlung wurde nicht gefunden.');
      return;
    }

    const amount = parseFloat(rueckzahlungForm.amount);
    if (amount <= 0 || amount > originalPayment.amount) {
      alert(`Der Rückzahlungsbetrag muss zwischen 0 und ${originalPayment.amount}€ liegen.`);
      return;
    }

    setIsAddingRueckzahlung(true);

    try {
      console.log('🔄 Creating refund with data:', {
        payment_id: rueckzahlungForm.payment_id,
        amount: amount,
        reason: rueckzahlungForm.reason,
        notes: rueckzahlungForm.notes
      });

      // Verwende 'manual_payment' als data_type da es bereits erlaubt ist
      // und markiere es als Rückzahlung durch ein spezielles Feld
      const refundData = {
        user_id: user.id,
        data_type: 'manual_payment',
        data: {
          type: 'refund', // Spezielle Markierung für Rückzahlungen
          payment_id: rueckzahlungForm.payment_id,
          original_payment_title: originalPayment.title,
          original_payment_amount: originalPayment.amount,
          refund_amount: amount,
          reason: rueckzahlungForm.reason.trim(),
          notes: rueckzahlungForm.notes.trim() || '',
          status: 'pending',
          refund_date: new Date().toISOString().split('T')[0],
          created_at: new Date().toISOString()
        },
        created_at: new Date().toISOString()
      };

      console.log('💾 Final refund data for insert:', refundData);

      const { data, error } = await supabase
        .from('daten')
        .insert([refundData])
        .select()
        .single();

      if (error) {
        console.error('❌ Database error details:', error);
        
        // Spezifische Fehlerbehandlung
        if (error.code === '23514') {
          throw new Error('Datenbank-Konfigurationsfehler. Bitte kontaktieren Sie den Support.');
        } else if (error.code === '23505') {
          throw new Error('Diese Rückzahlung existiert bereits.');
        } else if (error.code === '23503') {
          throw new Error('Die referenzierte Zahlung wurde nicht gefunden.');
        } else if (error.code === '42501') {
          throw new Error('Keine Berechtigung für diese Aktion.');
        } else {
          throw new Error(`Datenbankfehler: ${error.message} (Code: ${error.code})`);
        }
      }

      if (!data) {
        throw new Error('Keine Daten vom Server zurückerhalten');
      }

      console.log('✅ Refund created successfully:', data);

      // Lokalen State aktualisieren
      await loadRueckzahlungen();
      
      // Formular zurücksetzen
      setRueckzahlungForm({
        payment_id: '',
        amount: '',
        reason: '',
        notes: ''
      });
      
      setIsRueckzahlungModalOpen(false);
      alert('✅ Rückzahlung erfolgreich angefordert!');
    } catch (error) {
      console.error('❌ Error creating refund:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      alert(`Fehler beim Anfordern der Rückzahlung: ${errorMessage}`);
    } finally {
      setIsAddingRueckzahlung(false);
    }
  };

  // Rückzahlungen laden
  const loadRueckzahlungen = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'manual_payment')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading refunds:', error);
        return;
      }

      // Filtere nur die Einträge, die als Rückzahlungen markiert sind
      const refundData = (data || [])
        .filter(item => item.data && item.data.type === 'refund')
        .map(item => ({
          id: item.id,
          payment_id: item.data.payment_id,
          original_payment_title: item.data.original_payment_title,
          original_payment_amount: item.data.original_payment_amount,
          refund_amount: item.data.refund_amount,
          reason: item.data.reason,
          notes: item.data.notes || '',
          status: item.data.status || 'pending',
          refund_date: item.data.refund_date,
          created_at: item.created_at
        }));

      setRueckzahlungen(refundData);
    } catch (error) {
      console.error('Error loading refunds:', error);
    }
  };

  // Fixed costs calculation
  const calculateTotalFixedCosts = (costs: FixedCost[], monthlySales: Sale[], year: number, month: number) => {
    console.log('🧮 STARTING FIXED COSTS CALCULATION');
    console.log('🧮 Input costs:', costs.length);
    console.log('🧮 Month/Year:', `${year}-${month}`);

    let totalFixedCosts = 0;
    let totalFixedRevenue = 0;

    costs.forEach((cost, index) => {
      console.log(`\n🔍 [${index + 1}/${costs.length}] Processing: ${cost.name}`);
      console.log(`📊 Status: ${cost.status}, Amount: ${cost.amount}, Type: ${cost.costType}`);

      if (cost.status !== 'aktiv') {
        console.log(`❌ SKIP: Status not active (${cost.status})`);
        return;
      }

      let monthlyAmount = 0;

      if (cost.amount && cost.amount > 0) {
        if (cost.interval === 'monatlich') {
          monthlyAmount = cost.amount;
        } else if (cost.interval === 'jährlich') {
          monthlyAmount = cost.amount / 12;
        } else if (cost.interval === 'einmalig') {
          if (cost.startDate) {
            const startDate = new Date(cost.startDate);
            if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
              monthlyAmount = cost.amount;
            }
          } else {
            monthlyAmount = cost.amount;
          }
        }
      }

      if (cost.revenuePercentage && cost.revenuePercentage > 0) {
        let relevantRevenue = 0;

        if (cost.automatId) {
          relevantRevenue = monthlySales
            .filter(sale => sale.automatId === cost.automatId)
            .reduce((sum, sale) => sum + sale.totalPrice, 0);
        } else {
          relevantRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
        }

        const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
        monthlyAmount += revenueShareAmount;
      }

      if (cost.revenueAmount && cost.revenueAmount > 0) {
        monthlyAmount += cost.revenueAmount;
      }

      if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
        totalFixedRevenue += monthlyAmount;
        console.log(`✅ REVENUE: ${cost.name} = +${monthlyAmount.toFixed(2)} (Total Revenue: ${totalFixedRevenue.toFixed(2)})`);
      } else {
        totalFixedCosts += monthlyAmount;
        console.log(`❌ COST: ${cost.name} = -${monthlyAmount.toFixed(2)} (Total Costs: ${totalFixedCosts.toFixed(2)})`);
      }
    });

    console.log('\n🎯 FINAL CALCULATION RESULTS:');
    console.log(`💰 Total Fixed Costs: ${totalFixedCosts.toFixed(2)}`);
    console.log(`💚 Total Fixed Revenue: ${totalFixedRevenue.toFixed(2)}`);
    console.log(`📊 Net Fixed Impact: ${(totalFixedRevenue - totalFixedCosts).toFixed(2)}`);

    return { totalFixedCosts, totalFixedRevenue };
  };

  // Automaton performance calculations
  useEffect(() => {
    if (!fixedCosts || fixedCosts.length === 0) {
      console.log('⚠️ No fixed costs available for calculation');
      return;
    }

    console.log('\n🔄 RECALCULATING MONTHLY DATA');
    console.log('🔄 Available fixedCosts:', fixedCosts.length);
    console.log('🔄 Available sales:', sales.length);

    const year = parseInt(currentMonth.split('-')[0]);
    const month = parseInt(currentMonth.split('-')[1]);

    console.log(`📅 Calculating for: ${year}-${month}`);

    const monthlySales = sales.filter(sale => {
      const saleDate = new Date(sale.date);
      const saleYear = saleDate.getFullYear();
      const saleMonth = saleDate.getMonth() + 1;
      console.log(`🔍 Sale: ${sale.date} -> Year: ${saleYear}, Month: ${saleMonth}, Target: ${year}-${month}`);
      return saleYear === year && saleMonth === month;
    });

    console.log(`📊 Monthly sales count: ${monthlySales.length}`);
    console.log(
      `📊 Monthly sales:`,
      monthlySales.map(s => ({
        date: s.date,
        product: s.productName,
        amount: s.totalPrice,
        profit: s.profit
      }))
    );

    const monthlyManualPayments = manualPayments.filter(payment => {
      const paymentDate = new Date(payment.date);
      return paymentDate.getFullYear() === year && paymentDate.getMonth() + 1 === month;
    });

    const { totalFixedCosts, totalFixedRevenue } = calculateTotalFixedCosts(
      fixedCosts,
      monthlySales,
      year,
      month
    );

    const revenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
    const costs = monthlySales.reduce((sum, sale) => sum + (sale.totalPrice - sale.profit), 0);

    const manualIncome = monthlyManualPayments
      .filter(p => p.type === 'income')
      .reduce((sum, p) => sum + p.amount, 0);
    
    const manualExpenses = monthlyManualPayments
      .filter(p => p.type === 'expense')
      .reduce((sum, p) => sum + p.amount, 0);
    const manualPaymentsNet = manualIncome - manualExpenses;

    const grossProfit = revenue - costs;
    const netProfit = grossProfit + totalFixedRevenue - totalFixedCosts + manualPaymentsNet;
    const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

    const newMonthlyData = {
      month: currentMonth,
      revenue,
      costs,
      fixedCosts: totalFixedCosts,
      fixedRevenue: totalFixedRevenue,
      manualPayments: manualPaymentsNet,
      grossProfit,
      netProfit,
      netMargin
    };

    console.log('\n🎯 NEW MONTHLY DATA:');
    console.log('🎯 Revenue:', revenue.toFixed(2));
    console.log('🎯 Costs:', costs.toFixed(2));
    console.log('🎯 Fixed Costs:', totalFixedCosts.toFixed(2));
    console.log('🎯 Fixed Revenue:', totalFixedRevenue.toFixed(2));
    console.log('🎯 Net Profit:', netProfit.toFixed(2));

    setMonthlyData(newMonthlyData);

    const performance = automaten.map(automat => {
      console.log(`\n🤖 Calculating performance for: ${automat.name}`);

      const automatSales = monthlySales.filter(sale => sale.automatId === automat.id);
      const automatRevenue = automatSales.reduce((sum, sale) => sum + sale.totalPrice, 0);
      const automatCosts = automatSales.reduce((sum, sale) => sum + (sale.totalPrice - sale.profit), 0);
      const automatGrossProfit = automatRevenue - automatCosts;

      console.log(`📊 ${automat.name} - Revenue: ${automatRevenue}, Costs: ${automatCosts}, Gross: ${automatGrossProfit}`);

      const automobileSpecificCosts = fixedCosts.filter(
        cost => cost.automatId === automat.id && cost.status === 'aktiv'
      );

      const generalCosts = fixedCosts.filter(cost => !cost.automatId && cost.status === 'aktiv');

      const totalAutomatenRevenue = sales.filter(sale => {
        const saleDate = new Date(sale.date);
        return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
      }).reduce((sum, sale) => sum + sale.totalPrice, 0);
      const automatRevenueShareRatio = totalAutomatenRevenue > 0 ? automatRevenue / totalAutomatenRevenue : 1 / automaten.length;

      console.log(`📊 ${automat.name} - Revenue share: ${(automatRevenueShareRatio * 100).toFixed(2)}%`);

      let automatFixedCosts = 0;
      let automatFixedRevenue = 0;
      let automatRevenueShare = 0;

      automobileSpecificCosts.forEach(cost => {
        let monthlyAmount = 0;
        const isRevenue = cost.isRevenue || cost.costType === 'fixkosten-einnahme';

        if (cost.amount && cost.amount > 0) {
          if (cost.interval === 'monatlich') {
            monthlyAmount = cost.amount;
          } else if (cost.interval === 'jährlich') {
            monthlyAmount = cost.amount / 12;
          } else if (cost.interval === 'einmalig') {
            if (cost.startDate) {
              const startDate = new Date(cost.startDate);
              if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                monthlyAmount = cost.amount;
              }
            } else {
              monthlyAmount = cost.amount;
            }
          }
        }

        if (cost.revenuePercentage && cost.revenuePercentage > 0) {
          let relevantRevenue = 0;

          if (cost.automatId === automat.id) {
            relevantRevenue = automatRevenue;
          } else if (!cost.automatId) {
            relevantRevenue = automatRevenue;
          }

          if (relevantRevenue > 0) {
            const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
            automatRevenueShare += revenueShareAmount;
          }
        }

        if (cost.revenueAmount && cost.revenueAmount > 0) {
          if (cost.automatId === automat.id) {
            monthlyAmount += cost.revenueAmount;
          } else if (!cost.automatId) {
            monthlyAmount += cost.revenueAmount * automatRevenueShareRatio;
          }
        }

        if (isRevenue) {
          automatFixedRevenue += monthlyAmount;
        } else {
          automatFixedCosts += monthlyAmount;
        }
      });

      generalCosts.forEach(cost => {
        const isRevenue = cost.isRevenue || cost.costType === 'fixkosten-einnahme';
        let monthlyAmount = 0;

        if (cost.amount && cost.amount > 0) {
          if (cost.interval === 'monatlich') {
            monthlyAmount = cost.amount;
          } else if (cost.interval === 'jährlich') {
            monthlyAmount = cost.amount / 12;
          } else if (cost.interval === 'einmalig') {
            if (cost.startDate) {
              const startDate = new Date(cost.startDate);
              if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                monthlyAmount = cost.amount;
              }
            } else {
              monthlyAmount = cost.amount;
            }
          }
        }

        if (cost.revenuePercentage && cost.revenuePercentage > 0) {
          let relevantRevenue = 0;

          if (!cost.automatId) {
            relevantRevenue = automatRevenue;
          }

          if (relevantRevenue > 0) {
            const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
            monthlyAmount += revenueShareAmount;
          }
        }

        const proportionalAmount = monthlyAmount * automatRevenueShareRatio;
        if (isRevenue) {
          automatFixedRevenue += proportionalAmount;
        } else {
          automatFixedCosts += proportionalAmount;
        }
      });

      const automatNetProfit = automatGrossProfit + automatFixedRevenue - automatFixedCosts - automatRevenueShare;
      const netMargin = automatRevenue > 0 ? (automatNetProfit / automatRevenue) * 100 : 0;

      return {
        id: automat.id,
        name: automat.name,
        revenue: automatRevenue,
        costs: automatCosts,
        grossProfit: automatGrossProfit,
        fixedCosts: automatFixedCosts,
        fixedRevenue: automatFixedRevenue,
        revenueShare: automatRevenueShare,
        netProfit: automatNetProfit,
        netMargin
      };
    });

    console.log('🎯 Automaton performance calculated:', performance.length, 'entries');
    setAutomatonPerformance(performance);
  }, [fixedCosts, sales, manualPayments, currentMonth, automaten]);

  const formatCurrency = (amount: number) => {
    // KORRIGIERT: Sichere Formatierung mit NaN-Prüfung
    if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
      return 'CHF 0.00';
    }
    return `CHF ${amount.toFixed(2)}`;
  };

  // Berechne Monatsdaten für ein bestimmtes Jahr und Monat
  const calculateMonthlyData = (year: number, month: number) => {
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    
    // Filter sales for this month
    const monthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        const saleYear = saleDate.getFullYear();
        const saleMonth = saleDate.getMonth() + 1;
        return saleYear === year && saleMonth === month;
      } catch (err) {
        console.warn('⚠️ Ungültiges Verkaufsdatum:', sale.date);
        return false;
      }
    });

    // Filter manual payments for this month
    const monthlyManualPayments = manualPayments.filter(payment => {
      try {
        const paymentDate = new Date(payment.date);
        return paymentDate.getFullYear() === year && paymentDate.getMonth() + 1 === month;
      } catch (err) {
        console.warn('⚠️ Ungültiges Zahlungsdatum:', payment.date);
        return false;
      }
    });

    const revenue = monthlySales.reduce((sum, sale) => sum + (Number(sale.totalPrice) || 0), 0);
    const costs = monthlySales.reduce((sum, sale) => sum + ((Number(sale.totalPrice) || 0) - (Number(sale.profit) || 0)), 0);
    const grossProfit = revenue - costs;

    // Calculate fixed costs and revenue
    let totalFixedCosts = 0;
    let totalFixedRevenue = 0;

    fixedCosts.forEach(cost => {
      if (cost.status !== 'aktiv') return;

      // Date validation
      if (cost.startDate) {
        const startDate = new Date(cost.startDate);
        const costStartYear = startDate.getFullYear();
        const costStartMonth = startDate.getMonth() + 1;
        if (costStartYear > year || (costStartYear === year && costStartMonth > month)) {
          return;
        }
      }

      if (cost.endDate) {
        const endDate = new Date(cost.endDate);
        const costEndYear = endDate.getFullYear();
        const costEndMonth = endDate.getMonth() + 1;
        if (costEndYear < year || (costEndYear === year && costEndMonth < month)) {
          return;
        }
      }

      let monthlyAmount = 0;

      // Base amount calculation
      if (cost.amount && cost.amount > 0) {
        if (cost.interval === 'monatlich') {
          monthlyAmount = cost.amount;
        } else if (cost.interval === 'jährlich') {
          monthlyAmount = cost.amount / 12;
        } else if (cost.interval === 'einmalig') {
          if (cost.startDate) {
            const startDate = new Date(cost.startDate);
            if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
              monthlyAmount = cost.amount;
            }
          } else {
            monthlyAmount = cost.amount;
          }
        }
      }

      // Revenue percentage calculation
      if (cost.revenuePercentage && cost.revenuePercentage > 0) {
        let relevantRevenue = 0;

        if (cost.automatId) {
          relevantRevenue = monthlySales
            .filter(sale => sale.automatId === cost.automatId)
            .reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
        } else {
          relevantRevenue = monthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
        }

        const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
        monthlyAmount += revenueShareAmount;
      }

      if (cost.revenueAmount && cost.revenueAmount > 0) {
        monthlyAmount += cost.revenueAmount;
      }

      if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
        totalFixedRevenue += monthlyAmount;
      } else {
        totalFixedCosts += monthlyAmount;
      }
    });

    const manualIncome = monthlyManualPayments
      .filter(p => p.type === 'income')
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    
    const manualExpenses = monthlyManualPayments
      .filter(p => p.type === 'expense')
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const manualPaymentsNet = manualIncome - manualExpenses;

    const netProfit = grossProfit + totalFixedRevenue - totalFixedCosts + manualPaymentsNet;
    const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

    const currentMonthNumber = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return {
      month: monthStr,
      revenue,
      costs,
      fixedCosts: totalFixedCosts,
      fixedRevenue: totalFixedRevenue,
      manualPayments: manualPaymentsNet,
      grossProfit,
      netProfit,
      netMargin,
      isCurrentMonth: year === currentYear && month === currentMonthNumber,
      isFutureMonth: year > currentYear || (year === currentYear && month > currentMonthNumber),
      isPastMonth: year < currentYear || (year === currentYear && month < currentMonthNumber)
    };
  };

  // Berechne Automaten-spezifische Daten für ein bestimmtes Jahr und Monat
  const calculateAutomatMonthlyData = (automat: Automat, year: number, month: number) => {
    // Filter sales for this month and automat
    const automatMonthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        const saleYear = saleDate.getFullYear();
        const saleMonth = saleDate.getMonth() + 1;
        return saleYear === year && saleMonth === month && sale.automatId === automat.id;
      } catch (err) {
        return false;
      }
    });

    const automatRevenue = automatMonthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
    const automatCosts = automatMonthlySales.reduce((sum, sale) => sum + ((sale.totalPrice || 0) - (sale.profit || 0)), 0);
    const automatGrossProfit = automatRevenue - automatCosts;

    // Get all sales for this month (for percentage calculations)
    const allMonthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
      } catch (err) {
        return false;
      }
    });

    const totalAutomatenRevenue = allMonthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
    const automatRevenueShareRatio = totalAutomatenRevenue > 0 ? automatRevenue / totalAutomatenRevenue : 1 / automaten.length;

    let automatFixedCosts = 0;
    let automatFixedRevenue = 0;
    let automatRevenueShare = 0;

    fixedCosts.forEach(cost => {
      if (cost.status !== 'aktiv') return;

      // Date validation
      if (cost.startDate) {
        const startDate = new Date(cost.startDate);
        const costStartYear = startDate.getFullYear();
        const costStartMonth = startDate.getMonth() + 1;
        if (costStartYear > year || (costStartYear === year && costStartMonth > month)) {
          return;
        }
      }

      if (cost.endDate) {
        const endDate = new Date(cost.endDate);
        const costEndYear = endDate.getFullYear();
        const costEndMonth = endDate.getMonth() + 1;
        if (costEndYear < year || (costEndYear === year && costEndMonth < month)) {
          return;
        }
      }

      let monthlyAmount = 0;

      // Base amount calculation
      if (cost.amount && cost.amount > 0) {
        if (cost.interval === 'monatlich') {
          monthlyAmount = cost.amount;
        } else if (cost.interval === 'jährlich') {
          monthlyAmount = cost.amount / 12;
        } else if (cost.interval === 'einmalig') {
          if (cost.startDate) {
            const startDate = new Date(cost.startDate);
            if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
              monthlyAmount = cost.amount;
            }
          } else {
            monthlyAmount = cost.amount;
          }
        }
      }

      // Revenue percentage calculation
      if (cost.revenuePercentage && cost.revenuePercentage > 0) {
        let relevantRevenue = 0;

        if (cost.automatId === automat.id) {
          relevantRevenue = automatRevenue;
        } else if (!cost.automatId) {
          relevantRevenue = automatRevenue;
        }
        
        if (relevantRevenue > 0) {
          const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
          automatRevenueShare += revenueShareAmount;
        }
      }

      if (cost.revenueAmount && cost.revenueAmount > 0) {
        if (cost.automatId === automat.id) {
          monthlyAmount += cost.revenueAmount;
        } else if (!cost.automatId) {
          monthlyAmount += cost.revenueAmount * automatRevenueShareRatio;
        }
      }

      // Apply costs based on automat assignment
      if (cost.automatId === automat.id) {
        // Direct assignment to this automat
        if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
          automatFixedRevenue += monthlyAmount;
        } else {
          automatFixedCosts += monthlyAmount;
        }
      } else if (!cost.automatId) {
        // General costs - distribute proportionally
        const proportionalAmount = monthlyAmount * automatRevenueShareRatio;
        if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
          automatFixedRevenue += proportionalAmount;
        } else {
          automatFixedCosts += proportionalAmount;
        }
      }
    });

    const automatNetProfit = automatGrossProfit + automatFixedRevenue - automatFixedCosts - automatRevenueShare;
    const netMargin = automatRevenue > 0 ? (automatNetProfit / automatRevenue) * 100 : 0;

    const currentMonthNumber = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return {
      month: month,
      revenue: automatRevenue,
      costs: automatCosts,
      fixedRevenue: automatFixedRevenue,
      fixedCosts: automatFixedCosts,
      revenueShare: automatRevenueShare,
      netProfit: automatNetProfit,
      netMargin,
      isCurrentMonth: year === currentYear && month === currentMonthNumber,
      isFutureMonth: year > currentYear || (year === currentYear && month > currentMonthNumber),
      isPastMonth: year < currentYear || (year === currentYear && month < currentMonthNumber)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Finanzdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'overview'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Übersicht
              </button>
              <button
                onClick={() => setActiveTab('fixkosten')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'fixkosten'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Fixkosten
              </button>
              <button
                onClick={() => setActiveTab('zahlungen')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'zahlungen'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Manuelle Zahlungen
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'payments'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Zahlungen
              </button>
              <button
                onClick={() => navigate('/finanzplanung')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'investitionen'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-funds-line mr-1"></i>
                <span className="hidden sm:inline">Investitionen</span>
                <span className="sm:hidden">Invest.</span>
              </button>
              <button
                onClick={() => setActiveTab('budget')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'budget'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-calendar-check-line mr-1"></i>
                Budget
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                {/* Monatsauswahl */}
                <div className="mb-4 sm:mb-6">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Monat auswählen:
                  </label>
                  <input
                    type="month"
                    value={currentMonth}
                    onChange={e => setCurrentMonth(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-5 text-sm"
                  />
                </div>

                {/* DEBUG INFORMATION */}
                <div className="mb-4 p-3 bg-gray-50 border border-gray-100 rounded text-xs text-gray-500">
                  <details className="cursor-pointer">
                    <summary className="font-medium text-gray-600 hover:text-gray-800">
                      System-Status ({fixedCosts.length} Fixkosten, {manualPayments.length} Zahlungen)
                    </summary>
                    <div className="mt-2 space-y-1 text-gray-500">
                      <p>Aktive Fixkosten: {fixedCosts.filter(c => c.status === 'aktiv').length}</p>
                      <p>Berechnete Fixkosten: {formatCurrency(monthlyData.fixedCosts)}</p>
                      <p>Monat: {currentMonth}</p>
                    </div>
                  </details>
                </div>

                {/* Finanz-Übersicht Kacheln */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                  {/* Umsatz */}
                  <div className="bg-green-50 p-4 sm:p-6 rounded-lg border-2 border-green-200">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-sm sm:text-lg font-semibold text-green-800">Umsatz</h3>
                      <div className="p-2 bg-green-200 rounded-lg">
                        <i className="ri-money-dollar-circle-line text-green-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-sm sm:text-base"></i>
                      </div>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-green-600">{formatCurrency(monthlyData.revenue)}</p>
                    <p className="text-xs sm:text-sm text-green-600 mt-1">Gesamtumsatz im Monat</p>
                  </div>

                  {/* Fixe Einnahmen */}
                  <div className="bg-emerald-50 p-4 sm:p-6 rounded-lg border-2 border-emerald-200">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-sm sm:text-lg font-semibold text-emerald-800">Fixe Einnahmen</h3>
                      <div className="p-2 bg-emerald-200 rounded-lg">
                        <i className="ri-hand-coin-line text-emerald-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-sm sm:text-base"></i>
                      </div>
                    </div>

                    <p className="text-lg sm:text-2xl font-bold text-emerald-600">
                      {formatCurrency(
                        fixedCosts
                          .filter(cost => cost.status === 'aktiv' && (cost.isRevenue || cost.costType === 'fixkosten-einnahme'))
                          .reduce((sum, cost) => {
                            const year = parseInt(currentMonth.split('-')[0]);
                            const month = parseInt(currentMonth.split('-')[1]);
                            const monthlySales = sales.filter(sale => {
                              const saleDate = new Date(sale.date);
                              return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                            });

                            let monthlyAmount = 0;
                            if (cost.interval === 'monatlich') {
                              monthlyAmount = cost.amount || 0;
                            } else if (cost.interval === 'jährlich') {
                              monthlyAmount = (cost.amount || 0) / 12;
                            } else if (cost.interval === 'einmalig') {
                              if (cost.startDate) {
                                const startDate = new Date(cost.startDate);
                                if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                                  monthlyAmount = cost.amount || 0;
                                }
                              } else {
                                monthlyAmount = cost.amount || 0;
                              }
                            }

                            if (cost.revenuePercentage && cost.revenuePercentage > 0) {
                              let automatRevenue = 0;
                              if (cost.automatId) {
                                automatRevenue = monthlySales
                                  .filter(sale => sale.automatId === cost.automatId)
                                  .reduce((sum, sale) => sum + sale.totalPrice, 0);
                              } else {
                                automatRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
                              }
                              monthlyAmount += (automatRevenue * cost.revenuePercentage) / 100;
                            }

                            if (cost.revenueAmount && cost.revenueAmount > 0) {
                              monthlyAmount += cost.revenueAmount;
                            }

                            return sum + monthlyAmount;
                          }, 0)
                      )}
                    </p>
                    <p className="text-xs sm:text-sm text-emerald-600 mt-1">Zusätzliche Einnahmen</p>
                  </div>

                  {/* Fixkosten */}
                  <div className="bg-red-50 p-4 sm:p-6 rounded-lg border-2 border-red-200">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-sm sm:text-lg font-semibold text-red-800">Fixkosten</h3>
                      <div className="p-2 bg-red-200 rounded-lg">
                        <i className="ri-receipt-line text-red-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-sm sm:text-base"></i>
                      </div>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-red-600">{formatCurrency(monthlyData.fixedCosts)}</p>
                    <p className="text-xs sm:text-sm text-red-600 mt-1">Monatliche Fixkosten</p>
                  </div>

                  {/* Nettogewinn */}
                  <div
                    className={`p-4 sm:p-6 rounded-lg border-2 ${
                      monthlyData.netProfit >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3
                        className={`text-sm sm:text-lg font-semibold ${
                          monthlyData.netProfit >= 0 ? 'text-blue-800' : 'text-orange-800'
                        }`}
                      >
                        Nettogewinn
                      </h3>
                      <div
                        className={`p-2 rounded-lg ${
                          monthlyData.netProfit >= 0 ? 'bg-blue-200' : 'bg-orange-200'
                        }`}
                      >
                        <i
                          className={`ri-line-chart-line w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center ${
                            monthlyData.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'
                          } text-sm sm:text-base`}
                        ></i>
                      </div>
                    </div>
                    <p
                      className={`text-lg sm:text-2xl font-bold ${
                        monthlyData.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}
                    >
                      {formatCurrency(monthlyData.netProfit)}
                    </p>
                    <p
                      className={`text-xs sm:text-sm mt-1 ${
                        monthlyData.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}
                    >
                      {monthlyData.netProfit >= 0 ? 'Gewinn' : 'Verlust'} im Monat
                    </p>
                  </div>
                </div>

                {/* Detaillierte Aufschlüsselung */}
                <div className="bg-white border rounded-lg overflow-hidden mb-6 sm:mb-8">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Detaillierte Aufschlüsselung</h3>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 text-sm sm:text-base">Umsatz</span>
                        <span className="font-medium text-green-600 text-sm sm:text-base">+ {formatCurrency(monthlyData.revenue)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 text-sm sm:text-base">Wareneinsatz</span>
                        <span className="font-medium text-red-600 text-sm sm:text-base">- {formatCurrency(monthlyData.costs)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 font-medium text-sm sm:text-base">Bruttogewinn</span>
                        <span className="font-medium text-blue-600 text-sm sm:text-base">{formatCurrency(monthlyData.grossProfit)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 text-sm sm:text-base">Fixe Einnahmen</span>
                        <span className="font-medium text-emerald-600 text-sm sm:text-base">
                          +{formatCurrency(
                            fixedCosts
                              .filter(cost => cost.status === 'aktiv' && (cost.isRevenue || cost.costType === 'fixkosten-einnahme'))
                              .reduce((sum, cost) => {
                                const year = parseInt(currentMonth.split('-')[0]);
                                const month = parseInt(currentMonth.split('-')[1]);
                                const monthlySales = sales.filter(sale => {
                                  const saleDate = new Date(sale.date);
                                  return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                                });

                                let monthlyAmount = 0;
                                if (cost.interval === 'monatlich') {
                                  monthlyAmount = cost.amount || 0;
                                } else if (cost.interval === 'jährlich') {
                                  monthlyAmount = (cost.amount || 0) / 12;
                                } else if (cost.interval === 'einmalig') {
                                  if (cost.startDate) {
                                    const startDate = new Date(cost.startDate);
                                    if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                                      monthlyAmount = cost.amount || 0;
                                    }
                                  } else {
                                    monthlyAmount = cost.amount || 0;
                                  }
                                }

                                if (cost.revenuePercentage && cost.revenuePercentage > 0) {
                                  let automatRevenue = 0;
                                  if (cost.automatId) {
                                    automatRevenue = monthlySales
                                      .filter(sale => sale.automatId === cost.automatId)
                                      .reduce((sum, sale) => sum + sale.totalPrice, 0);
                                  } else {
                                    automatRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
                                  }
                                  monthlyAmount += (automatRevenue * cost.revenuePercentage) / 100;
                                }

                                if (cost.revenueAmount && cost.revenueAmount > 0) {
                                  monthlyAmount += cost.revenueAmount;
                                }

                                return sum + monthlyAmount;
                              }, 0)
                          )}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 text-sm sm:text-base">Fixkosten</span>
                        <span className="font-medium text-red-600 text-sm sm:text-base">{formatCurrency(monthlyData.fixedCosts)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-700 text-sm sm:text-base">Manuelle Zahlungen (Netto)</span>
                        <span
                          className={`font-medium text-sm sm:text-base ${
                            monthlyData.manualPayments >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {monthlyData.manualPayments >= 0 ? '+' : ''}
                          {formatCurrency(monthlyData.manualPayments)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-3 border-t-2 border-gray-200 bg-gray-50 -mx-4 sm:-mx-6 px-4 sm:px-6">
                        <span className="text-base sm:text-lg font-bold text-gray-900">Nettogewinn</span>
                        <span className="text-lg sm:text-xl font-bold">
                          {formatCurrency(monthlyData.netProfit)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Automaten Performance */}
                {automatonPerformance.length > 0 && (
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Automaten Performance</h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        Finanzübersicht für jeden Automaten mit getrennter Umsatzbeteiligung und fixen Einnahmen
                      </p>
                    </div>
                    <div className="p-4 sm:p-6">
                      <div className="space-y-4 sm:space-y-6">
                        {automatonPerformance.map(automat => (
                          <div key={automat.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
                            {/* Automat Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
                              <div className="flex items-center">
                                <div className="p-2 bg-gray-200 rounded-lg mr-3">
                                  <i className="ri-robot-line text-gray-600 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-sm sm:text-base"></i>
                                </div>
                                <div>
                                  <h4 className="text-sm sm:text-lg font-medium text-gray-900">{automat.name}</h4>
                                </div>
                              </div>
                              <div className="text-left sm:text-right">
                                <div
                                  className={`text-lg sm:text-xl font-bold ${
                                    automat.netProfit > 0 ? 'text-green-600' : automat.netProfit < 0 ? 'text-red-600' : 'text-yellow-600'
                                  }`}
                                >
                                  {formatCurrency(automat.netProfit)}
                                </div>
                                <div className="text-xs text-gray-500">Nettogewinn</div>
                              </div>
                            </div>

                            {/* Mobile: Stacked View */}
                            <div className="block sm:hidden space-y-2">
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Umsatz</span>
                                <span className="text-sm font-medium text-gray-700">{formatCurrency(automat.revenue)}</span>
                              </div>
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Wareneinsatz</span>
                                <span className="text-sm font-medium text-gray-700">-{formatCurrency(automat.costs)}</span>
                              </div>
                              <div className="flex justify-between py-2 border-b border-gray-100 bg-blue-25">
                                <span className="text-sm text-gray-600 font-medium">Bruttogewinn</span>
                                <span className="text-sm font-medium text-blue-700">{formatCurrency(automat.grossProfit)}</span>
                              </div>
                              {automat.revenueShare > 0 && (
                                <div className="flex justify-between py-2 border-b border-gray-100 bg-red-25">
                                  <span className="text-sm text-red-700 font-medium">
                                    <i className="ri-line-chart-line mr-1"></i>
                                    Umsatzbeteiligung
                                  </span>
                                  <span className="text-sm font-bold text-red-700">
                                    -{formatCurrency(automat.revenueShare)}
                                  </span>
                                </div>
                              )}
                              {automat.fixedRevenue > 0 && (
                                <div className="flex justify-between py-2 border-b border-gray-100 bg-green-25">
                                  <span className="text-sm text-green-700 font-medium">
                                    <i className="ri-money-dollar-circle-line mr-1"></i>
                                    Fixe Einnahmen
                                  </span>
                                  <span className="text-sm font-bold text-green-700">
                                    +{formatCurrency(automat.fixedRevenue)}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Fixkosten</span>
                                <span className="text-sm font-medium text-gray-700">-{formatCurrency(automat.fixedCosts)}</span>
                              </div>
                              <div className="flex justify-between py-2 bg-gray-100 rounded">
                                <span className="text-sm font-bold text-gray-900">Nettogewinn</span>
                                <span
                                  className={`text-sm font-bold ${
                                    automat.netProfit > 0 ? 'text-green-600' : automat.netProfit < 0 ? 'text-red-600' : 'text-yellow-600'
                                  }`}
                                >
                                  {formatCurrency(automat.netProfit)}
                                </span>
                              </div>
                            </div>

                            {/* Desktop: Table View */}
                            <div className="hidden sm:block bg-white rounded border overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  <tr className="border-b border-gray-100">
                                    <td className="px-3 py-2 text-gray-600">Umsatz</td>
                                    <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(automat.revenue)}</td>
                                  </tr>
                                  <tr className="border-b border-gray-100">
                                    <td className="px-3 py-2 text-gray-600">Wareneinsatz</td>
                                    <td className="px-3 py-2 text-right text-gray-700">-{formatCurrency(automat.costs)}</td>
                                  </tr>
                                  <tr className="border-b border-gray-100 bg-blue-25">
                                    <td className="px-3 py-2 text-gray-600 font-medium">Bruttogewinn</td>
                                    <td className="px-3 py-2 text-right text-blue-700 font-medium">{formatCurrency(automat.grossProfit)}</td>
                                  </tr>
                                  {automat.revenueShare > 0 && (
                                    <tr className="border-b border-gray-100 bg-red-25">
                                      <td className="px-3 py-2 text-red-700 font-medium">
                                        <i className="ri-line-chart-line mr-1"></i>
                                        Umsatzbeteiligung
                                      </td>
                                      <td className="px-3 py-2 text-right text-red-700 font-bold">
                                        -{formatCurrency(automat.revenueShare)}
                                      </td>
                                    </tr>
                                  )}
                                  {automat.fixedRevenue > 0 && (
                                    <tr className="border-b border-gray-100 bg-green-25">
                                      <td className="px-3 py-2 text-green-700 font-medium">
                                        <i className="ri-money-dollar-circle-line mr-1"></i>
                                        Fixe Einnahmen
                                      </td>
                                      <td className="px-3 py-2 text-right text-green-700 font-bold">
                                        +{formatCurrency(automat.fixedRevenue)}
                                      </td>
                                    </tr>
                                  )}
                                  <tr className="border-b border-gray-100">
                                    <td className="px-3 py-2 text-gray-600">Fixkosten</td>
                                    <td className="px-3 py-2 text-right text-gray-700">-{formatCurrency(automat.fixedCosts)}</td>
                                  </tr>
                                  <tr className="bg-gray-100">
                                    <td className="px-3 py-2 font-bold text-gray-900">Nettogewinn</td>
                                    <td
                                      className={`px-3 py-2 text-right font-bold ${
                                        automat.netProfit > 0 ? 'text-green-600' : automat.netProfit < 0 ? 'text-red-600' : 'text-yellow-600'
                                      }`}
                                    >
                                      {formatCurrency(automat.netProfit)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Fixkosten Tab */}
            {activeTab === 'fixkosten' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Fixkosten verwalten
                  </h2>
                  <p className="text-gray-600 text-xs sm:text-base">
                    Erstellen und verwalten Sie Ihre Fixkosten für verschiedene Automaten.
                  </p>
                </div>

                <FixedCostForm automaten={automaten} onSave={handleFixedCostsSave} />

                {/* Vorhandene Fixkosten anzeigen */}
                {fixedCosts.length > 0 && (
                  <div className="mt-6 sm:mt-8 bg-white border-2 border-gray-200 rounded-lg">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                          <i className="ri-list-check mr-2"></i>
                          Vorhandene Fixkosten ({fixedCosts.length})
                        </h3>
                        <button
                          onClick={async () => {
                            if (
                              window.confirm(
                                'Möchten Sie wirklich alle Fixkosten löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
                              )
                            ) {
                              try {
                                const { error } = await supabase.from('fixkosten').delete().eq('user_id', user?.id);
                                if (error) throw error;
                                await loadAllData();
                                alert('Alle Fixkosten wurden erfolgreich gelöscht.');
                              } catch (error) {
                                console.error('Error deleting fixed costs:', error);
                                alert('Fehler beim Löschen der Fixkosten.');
                              }
                            }
                          }}
                          className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 whitespace-nowrap flex items-center justify-center text-xs sm:text-sm"
                        >
                          <i className="ri-delete-bin-line mr-2"></i>
                          Alle Fixkosten löschen
                        </button>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      {(() => {
                        // Gruppiere Fixkosten nach Automaten
                        const groupedCosts = fixedCosts.reduce((groups, cost) => {
                          const automatName = cost.automatName || 'Allgemeine Fixkosten';
                          if (!groups[automatName]) {
                            groups[automatName] = [];
                          }
                          groups[automatName].push(cost);
                          return groups;
                        }, {} as Record<string, FixedCost[]>);

                        // Berechne Saldo für einen Automaten
                        const calculateAutomatSaldo = (costs: FixedCost[]) => {
                          const year = parseInt(currentMonth.split('-')[0]);
                          const month = parseInt(currentMonth.split('-')[1]);
                          const monthlySales = sales.filter(sale => {
                            const saleDate = new Date(sale.date);
                            return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                          });

                          return costs.reduce((sum, cost) => {
                            if (cost.status !== 'aktiv') return sum;

                            let monthlyAmount = 0;

                            if (cost.amount && cost.amount > 0) {
                              if (cost.interval === 'monatlich') {
                                monthlyAmount = cost.amount;
                              } else if (cost.interval === 'jährlich') {
                                monthlyAmount = cost.amount / 12;
                              } else if (cost.interval === 'einmalig') {
                                if (cost.startDate) {
                                  const startDate = new Date(cost.startDate);
                                  if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                                    monthlyAmount = cost.amount;
                                  }
                                } else {
                                  monthlyAmount = cost.amount;
                                }
                              }
                            }

                            if (cost.revenuePercentage && cost.revenuePercentage > 0) {
                              let relevantRevenue = 0;

                              if (cost.automatId) {
                                relevantRevenue = monthlySales
                                  .filter(sale => sale.automatId === cost.automatId)
                                  .reduce((sum, sale) => sum + sale.totalPrice, 0);
                              } else {
                                relevantRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
                              }

                              const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
                              monthlyAmount += revenueShareAmount;
                            }

                            if (cost.revenueAmount && cost.revenueAmount > 0) {
                              monthlyAmount += cost.revenueAmount;
                            }

                            return cost.isRevenue || cost.costType === 'fixkosten-einnahme' ? sum + monthlyAmount : sum - monthlyAmount;
                          }, 0);
                        };

                        // Einzelne Fixkosten mit Umsatzbeteiligung
                        const calculateIndividualCost = (cost: FixedCost) => {
                          const year = parseInt(currentMonth.split('-')[0]);
                          const month = parseInt(currentMonth.split('-')[1]);
                          const monthlySales = sales.filter(sale => {
                            const saleDate = new Date(sale.date);
                            return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                          });

                          let monthlyAmount = 0;
                          let revenueShareAmount = 0;

                          if (cost.amount && cost.amount > 0) {
                            if (cost.interval === 'monatlich') {
                              monthlyAmount = cost.amount;
                            } else if (cost.interval === 'jährlich') {
                              monthlyAmount = cost.amount / 12;
                            } else if (cost.interval === 'einmalig') {
                              if (cost.startDate) {
                                const startDate = new Date(cost.startDate);
                                if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                                  monthlyAmount = cost.amount;
                                }
                              } else {
                                monthlyAmount = cost.amount;
                              }
                            }
                          }

                          if (cost.revenuePercentage && cost.revenuePercentage > 0) {
                            const year = parseInt(currentMonth.split('-')[0]);
                            const month = parseInt(currentMonth.split('-')[1]);
                            const monthlySales = sales.filter(sale => {
                              const saleDate = new Date(sale.date);
                              return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                            });

                            let relevantRevenue = 0;
                            if (cost.automatId) {
                              relevantRevenue = monthlySales
                                .filter(sale => sale.automatId === cost.automatId)
                                .reduce((sum, sale) => sum + sale.totalPrice, 0);
                            } else {
                              relevantRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
                            }

                            revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
                            monthlyAmount += revenueShareAmount;
                          }

                          if (cost.revenueAmount && cost.revenueAmount > 0) {
                            monthlyAmount += cost.revenueAmount;
                            revenueShareAmount += cost.revenueAmount;
                          }

                          if ((!cost.amount || cost.amount === 0) && (!cost.revenuePercentage || cost.revenuePercentage === 0)) {
                            // Ensure we still display items with no amount or revenue share
                            monthlyAmount = 0;
                          }

                          return { monthlyAmount, revenueShareAmount };
                        };

                        // Funktion zum Hinzufügen einer neuen Fixkosten zu einem bestehenden Automaten
                        const handleAddFixedCostToAutomat = async (automatId: string | null, automatName: string) => {
                          const name = prompt('Name der neuen Fixkosten:');
                          if (!name) return;

                          const amountStr = prompt('Betrag (CHF):');
                          if (!amountStr) return;

                          const amount = parseFloat(amountStr);
                          if (isNaN(amount)) {
                            alert('Bitte geben Sie einen gültigen Betrag ein.');
                            return;
                          }

                          const typeChoice = confirm('Ist dies eine Einnahme? (OK = Ja, Abbrechen = Nein/Kosten)');
                          const costType = typeChoice ? 'fixkosten-einnahme' : 'fixkosten';
                          
                          try {
                            const fixedCostData = {
                              user_id: user?.id,
                              automat_id: automatId,
                              automat_name: automatName,
                              name: name,
                              category: 'aktive-fixkosten',
                              cost_type: costType,
                              amount: amount,
                              interval: 'monatlich',
                              start_date: new Date().toISOString().split('T')[0],
                              end_date: null,
                              description: '',
                              is_revenue: typeChoice,
                              revenue_percentage: null,
                              revenue_amount: null,
                              notes: '',
                              status: 'aktiv'
                            };

                            const { error } = await supabase
                              .from('fixkosten')
                              .insert([fixedCostData]);

                            if (error) throw error;

                            await loadAllData(); // Daten neu laden
                            alert('Neue Fixkosten wurde erfolgreich hinzugefügt!');
                          } catch (error) {
                            console.error('Error adding fixed cost:', error);
                            alert('Fehler beim Hinzufügen der Fixkosten.');
                          }
                        };

                        // Render grouped costs
                        return (
                          <div className="space-y-4 sm:space-y-6">
                            {Object.entries(groupedCosts).map(([automatName, costs]) => {
                              const saldo = calculateAutomatSaldo(costs);
                              const automatId = costs[0]?.automatId || null;
                              
                              return (
                                <div key={automatName} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                                  {/* Header */}
                                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                                      <div className="flex items-center">
                                        <i className="ri-robot-line text-gray-600 mr-2 text-base"></i>
                                        <h4 className="text-sm sm:text-lg font-medium text-gray-900">{automatName}</h4>
                                        <span className="ml-2 text-xs sm:text-sm text-gray-500">
                                          ({costs.length} Positionen)
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between sm:justify-end space-x-3">
                                        <div className="text-left sm:text-right">
                                          <span className="text-xs sm:text-sm text-gray-600">Monatlicher Saldo:</span>
                                          <div
                                            className={`text-sm sm:text-lg font-bold ml-2 inline-block ${
                                              saldo >= 0 ? 'text-green-600' : 'text-red-600'
                                            }`}
                                          >{saldo >= 0 ? '+' : ''}{formatCurrency(saldo)}</div>
                                        </div>
                                        
                                        {/* Neue Fixkosten hinzufügen Button */}
                                        <button
                                          onClick={() => handleAddFixedCostToAutomat(automatId, automatName)}
                                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg cursor-pointer transition-colors"
                                          title="Neue Fixkosten hinzufügen"
                                        >
                                          <i className="ri-add-circle-line text-lg"></i>
                                        </button>
                                        
                                        <button
                                          onClick={() => {
                                            const hiddenElements = document.querySelectorAll(`[data-automat="${automatName}"]`);
                                            hiddenElements.forEach(el => {
                                              const isHidden = (el as HTMLElement).style.display === 'none';
                                              (el as HTMLElement).style.display = isHidden ? 'block' : 'none';
                                            });
                                          }}
                                          className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                                          title="Bearbeiten"
                                        >
                                          <i className="ri-tools-line text-lg"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Kostenliste */}
                                  <div className="divide-y divide-gray-200">
                                    {(() => {
                                      const displayItems: any[] = [];
                                      
                                      costs.forEach(cost => {
                                        const isRevenue = cost.isRevenue || cost.costType === 'fixkosten-einnahme';
                                        const { monthlyAmount } = calculateIndividualCost(cost);
                                        
                                        // Nur Hauptkosten-Eintrag hinzufügen, wenn es einen festen Betrag gibt
                                        if (cost.amount && cost.amount > 0) {
                                          let baseAmount = 0;
                                          if (cost.interval === 'monatlich') {
                                            baseAmount = cost.amount;
                                          } else if (cost.interval === 'jährlich') {
                                            baseAmount = cost.amount / 12;
                                          } else if (cost.interval === 'einmalig') {
                                            const year = parseInt(currentMonth.split('-')[0]);
                                            const month = parseInt(currentMonth.split('-')[1]);
                                            if (cost.startDate) {
                                              const startDate = new Date(cost.startDate);
                                              if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
                                                baseAmount = cost.amount || 0;
                                              }
                                            } else {
                                              baseAmount = cost.amount || 0;
                                            }
                                          }

                                          if (baseAmount > 0) {
                                            displayItems.push({
                                              id: cost.id,
                                              type: 'main',
                                              cost: cost,
                                              amount: baseAmount,
                                              isRevenue: isRevenue
                                            });
                                          }
                                        }
                                        
                                        // Umsatzbeteiligung als separaten Eintrag nur hinzufügen, wenn es eine Umsatzbeteiligung gibt
                                        if (cost.revenuePercentage && cost.revenuePercentage > 0) {
                                          const year = parseInt(currentMonth.split('-')[0]);
                                          const month = parseInt(currentMonth.split('-')[1]);
                                          const monthlySales = sales.filter(sale => {
                                            const saleDate = new Date(sale.date);
                                            return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
                                          });
                                          
                                          let relevantRevenue = 0;
                                          if (cost.automatId) {
                                            relevantRevenue = monthlySales
                                              .filter(sale => sale.automatId === cost.automatId)
                                              .reduce((sum, sale) => sum + sale.totalPrice, 0);
                                          } else {
                                            relevantRevenue = monthlySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
                                          }

                                          const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
                                          
                                          if (revenueShareAmount > 0) {
                                            displayItems.push({
                                              id: `${cost.id}_revenue_share`,
                                              type: 'revenue_share',
                                              cost: cost,
                                              amount: revenueShareAmount,
                                              isRevenue: false,
                                              percentage: cost.revenuePercentage,
                                              baseRevenue: relevantRevenue
                                            });
                                          }
                                        }

                                        // Wenn weder fester Betrag noch Umsatzbeteiligung vorhanden ist, trotzdem anzeigen
                                        if ((!cost.amount || cost.amount === 0) && (!cost.revenuePercentage || cost.revenuePercentage === 0)) {
                                          displayItems.push({
                                            id: cost.id,
                                            type: 'main',
                                            cost: cost,
                                            amount: 0,
                                            isRevenue: isRevenue
                                          });
                                        }
                                      });
                                      
                                      return displayItems.map(item => (
                                        <div key={item.id} className="px-4 py-3 hover:bg-white transition-colors">
                                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                                            {/* Linke Seite */}
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center space-x-3">
                                                <div
                                                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                                                    item.type === 'revenue_share' 
                                                      ? 'bg-orange-500' 
                                                      : item.isRevenue 
                                                        ? 'bg-green-500' 
                                                        : 'bg-red-500'
                                                  }`}
                                                ></div>
                                                <div>
                                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                                    {item.cost.name}
                                                  </h4>
                                                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                                                    <span>
                                                      {item.type === 'revenue_share' 
                                                        ? `(${item.percentage}% der Umsätze von CHF ${item.baseRevenue.toFixed(2)})` 
                                                        : item.cost.interval}
                                                    </span>
                                                    {item.cost.description && (
                                                      <span className="hidden sm:inline">• {item.cost.description}</span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {/* Rechte Seite */}
                                            <div className="flex items-center space-x-3">
                                              <span
                                                className={`font-medium ${
                                                  item.type === 'revenue_share' 
                                                    ? 'text-orange-600' 
                                                    : item.isRevenue 
                                                      ? 'text-green-600' 
                                                      : 'text-red-600'
                                                }`}
                                              >
                                                {item.isRevenue && item.type !== 'revenue_share' ? '+' : ''}
                                                {formatCurrency(item.amount)}
                                              </span>
                                              {/* Nur für Hauptkosten (nicht für Umsatzbeteiligung) Bearbeiten/Löschen anzeigen */}
                                              {item.type === 'main' && (
                                                <>
                                                  <button
                                                    onClick={() => handleEditFixedCost(item.cost)}
                                                    className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
                                                    title="Bearbeiten"
                                                  >
                                                    <i className="ri-edit-line"></i>
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteFixedCost(item.cost.id, item.cost.name)}
                                                    className="p-1 text-gray-400 hover:text-red-600 cursor-pointer"
                                                    title="Löschen"
                                                  >
                                                    <i className="ri-delete-bin-line"></i>
                                                  </button>
                                                </>
                                              )}
                                              {/* Für Umsatzbeteiligung nur Info-Icon */}
                                              {item.type === 'revenue_share' && (
                                                <div className="p-1 text-orange-400" title="Umsatzbeteiligung - wird automatisch berechnet">
                                                  <i className="ri-information-line"></i>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {item.cost.description && (
                                            <div className="mt-1 text-xs text-gray-500 sm:hidden">
                                              {item.cost.description}
                                            </div>
                                          )}
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other tabs would continue here */}
            {activeTab === 'zahlungen' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Manuelle Zahlungen
                  </h2>
                  <p className="text-gray-600 text-xs sm:text-base">
                    Erfassen Sie manuelle Einnahmen und Ausgaben.
                  </p>
                </div>
                <ManualPaymentsForm onSave={async () => {
                  await loadAllData();
                  setActiveTab('overview');
                }} />
              </div>
            )}
            
            {activeTab === 'payments' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Zahlungen verwalten
                  </h2>
                  <p className="text-gray-600 text-xs sm:text-base">
                    Verwalten Sie alle Zahlungen und Rückzahlungen.
                  </p>
                </div>
                <PaymentsManager 
                  payments={manualPayments} 
                  refunds={rueckzahlungen}
                  onPaymentSaved={loadAllData}
                  onRefundSaved={loadRueckzahlungen}
                />
              </div>
            )}
            
            {activeTab === 'investitionen' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Investitionen
                      </h2>
                      <p className="text-gray-600 text-xs sm:text-base">
                        Verwalten Sie Ihre Investitionen und deren Abschreibungen.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        resetInvestmentForm();
                        setEditingInvestment(null);
                        setIsInvestmentModalOpen(true);
                      }}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Neue Investition
                    </button>
                  </div>
                </div>
                
                {/* Investments list */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {investments.map(investment => (
                    <div key={investment.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-medium text-gray-900">{investment.name}</h3>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleEditInvestment(investment)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Bearbeiten"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteInvestment(investment.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Löschen"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Kaufdatum:</span>
                          <span>{investment.purchaseDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Kaufpreis:</span>
                          <span className="font-medium">{formatCurrency(investment.purchasePrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Abschreibungsmethode:</span>
                          <span>{investment.depreciationMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Aktueller Wert:</span>
                          <span className="font-medium">{formatCurrency(investment.currentValue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Status:</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            investment.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                            investment.status === 'vollständig-abgeschrieben' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {investment.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Investment Modal */}
                <Modal
                  isOpen={isInvestmentModalOpen}
                  onClose={() => {
                    setIsInvestmentModalOpen(false);
                    setEditingInvestment(null);
                  }}
                  title={editingInvestment ? "Investition bearbeiten" : "Neue Investition"}
                >
                  <form onSubmit={handleInvestmentSubmit} className="space-y-4">
                    <Input
                      label="Name"
                      value={investmentFormData.name}
                      onChange={(e) => handleInvestmentInputChange('name', e.target.value)}
                      required
                    />
                    
                    <Input
                      label="Beschreibung"
                      value={investmentFormData.description}
                      onChange={(e) => handleInvestmentInputChange('description', e.target.value)}
                    />
                    
                    <Select
                      label="Kategorie"
                      options={investmentCategories.map(cat => ({ value: cat, label: cat }))}
                      value={investmentFormData.category}
                      onChange={(e) => handleInvestmentInputChange('category', e.target.value)}
                      required
                    />
                    
                    <Input
                      label="Kaufdatum"
                      type="date"
                      value={investmentFormData.purchaseDate}
                      onChange={(e) => handleInvestmentInputChange('purchaseDate', e.target.value)}
                      required
                    />
                    
                    <Input
                      label="Kaufpreis (CHF)"
                      type="number"
                      step="0.01"
                      value={investmentFormData.purchasePrice}
                      onChange={(e) => handleInvestmentInputChange('purchasePrice', e.target.value)}
                      required
                    />
                    
                    <Input
                      label="Nutzungsdauer (Jahre)"
                      type="number"
                      value={investmentFormData.usefulLife}
                      onChange={(e) => handleInvestmentInputChange('usefulLife', e.target.value)}
                      required
                    />
                    
                    <Select
                      label="Abschreibungsmethode"
                      options={depreciationMethods}
                      value={investmentFormData.depreciationMethod}
                      onChange={(e) => handleInvestmentInputChange('depreciationMethod', e.target.value)}
                      required
                    />
                    
                    <Select
                      label="Automat (optional)"
                      options={[
                        { value: '', label: 'Kein Automat' },
                        ...automaten.map(automat => ({
                          value: automat.id,
                          label: automat.name
                        }))
                      ]}
                      value={investmentFormData.automatId}
                      onChange={(e) => handleInvestmentInputChange('automatId', e.target.value)}
                    />
                    
                    <Input
                      label="Lieferant"
                      value={investmentFormData.supplier}
                      onChange={(e) => handleInvestmentInputChange('supplier', e.target.value)}
                    />
                    
                    <Input
                      label="Rechnungsnummer"
                      value={investmentFormData.invoiceNumber}
                      onChange={(e) => handleInvestmentInputChange('invoiceNumber', e.target.value)}
                    />
                    
                    <Input
                      label="Standort"
                      value={investmentFormData.location}
                      onChange={(e) => handleInvestmentInputChange('location', e.target.value)}
                    />
                    
                    <Input
                      label="Monatliche Einnahmen (CHF, optional)"
                      type="number"
                      step="0.01"
                      value={investmentFormData.monthlyRevenue}
                      onChange={(e) => handleInvestmentInputChange('monthlyRevenue', e.target.value)}
                    />
                    
                    <Input
                      label="Notizen"
                      value={investmentFormData.notes}
                      onChange={(e) => handleInvestmentInputChange('notes', e.target.value)}
                    />
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setIsInvestmentModalOpen(false);
                          setEditingInvestment(null);
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button type="submit">
                        {editingInvestment ? "Aktualisieren" : "Speichern"}
                      </Button>
                    </div>
                  </form>
                </Modal>
              </div>
            )}
            
            {activeTab === 'budget' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                        <i className="ri-calendar-check-line mr-3 text-blue-600"></i>
                        Budget-Übersicht
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Monatliche Finanzübersicht mit automatischer Monatserkennung und detaillierter Aufschlüsselung
                      </p>
                    </div>
                  </div>
                </div>

                {/* Datenübersicht */}
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-database-2-line text-green-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-green-900 mb-1">Datenübersicht</h4>
                      <div className="text-sm text-green-800 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>📊 Verkäufe: <strong>{sales.length}</strong></div>
                        <div>💰 Fixkosten: <strong>{fixedCosts.length}</strong></div>
                        <div>💳 Zahlungen: <strong>{manualPayments.length}</strong></div>
                        <div>🤖 Automaten: <strong>{automaten.length}</strong></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Jahresauswahl */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jahr auswählen:
                  </label>
                  <select
                    value={new Date().getFullYear()}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {(() => {
                      const years = [];
                      const currentYear = new Date().getFullYear();
                      for (let i = currentYear - 2; i <= currentYear + 1; i++) {
                        years.push(i);
                      }
                      return years.map(year => (
                        <option key={year} value={year}>{year}</option> 
                      ));
                    })()}
                  </select>
                </div>

                {/* Automatische Monatserkennung Status */}
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-calendar-check-line text-green-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-green-900 mb-1">Automatische Monatserkennung aktiv</h4>
                      <p className="text-sm text-green-800">
                        Aktueller Monat: <strong>{(() => {
                          const monthNumber = parseInt(currentMonth.split('-')[1]);
                          const year = parseInt(currentMonth.split('-')[0]);
                          const monthNames = [
                            'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                            'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                          ];
                          return `${monthNames[monthNumber - 1]} ${year}`;
                        })()}</strong> - Das System erkennt automatisch Monatswechsel und übernimmt alle Daten aus der Finanz-Übersicht
                      </p>
                    </div>
                  </div>
                </div>

                {/* Budget-Tabelle */}
                <div className="bg-white border rounded-lg overflow-hidden mb-8">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Monatliche Budget-Übersicht {new Date().getFullYear()}</h3>
                    <p className="text-sm text-gray-600 mt-1">Detaillierte Aufschlüsselung aller Finanzen pro Monat - automatisch aktualisiert</p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monat</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Umsatz</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Wareneinsatz</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fixe Einnahmen</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fixkosten</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Manuelle Zahlungen</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Nettogewinn</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Marge</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          const monthlyResults = [];

                          // Generate data for all 12 months
                          for (let month = 1; month <= 12; month++) {
                            const monthData = calculateMonthlyData(currentYear, month);
                            monthlyResults.push(monthData);
                          }

                          return monthlyResults.map((monthData, index) => (
                            <tr key={monthData.month} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${monthData.isCurrentMonth ? 'bg-blue-50 border-blue-200' : ''} ${monthData.isFutureMonth ? 'opacity-60' : ''} ${monthData.isPastMonth ? 'bg-gray-100' : ''}`}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className={`w-3 h-3 rounded-full mr-3 ${monthData.isFutureMonth ? 'bg-gray-300' : monthData.isPastMonth ? 'bg-gray-400' : monthData.netProfit > 0 ? 'bg-green-500' : monthData.netProfit < 0 ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900 flex items-center">
                                        {(() => {
                                          const monthNumber = parseInt(monthData.month.split('-')[1]);
                                          const year = parseInt(monthData.month.split('-')[0]);
                                          const monthNames = [
                                            'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                                            'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                                          ];
                                          return `${monthNames[monthNumber - 1]} ${year}`;
                                        })()}
                                        {monthData.isCurrentMonth && (
                                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                            ✓ Aktuell
                                          </span>
                                        )}
                                        {monthData.isFutureMonth && (
                                          <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                            ⏳ Zukunft
                                          </span>
                                        )}
                                        {monthData.isPastMonth && (
                                          <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                                            📅 Vergangen
                                          </span>
                                        )}
                                      </div>
                                      {(monthData.revenue > 0 || monthData.netProfit !== 0) && !monthData.isFutureMonth && (
                                        <div className="text-xs text-gray-500">Daten verfügbar</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{formatCurrency(monthData.revenue)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.costs)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-green-600">+{formatCurrency(monthData.fixedRevenue)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.fixedCosts)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  <span className={monthData.manualPayments >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {monthData.manualPayments >= 0 ? '+' : ''}
                                    {formatCurrency(monthData.manualPayments)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold">
                                  <span className={monthData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {formatCurrency(monthData.netProfit)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  <span className={monthData.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {monthData.netMargin.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Automaten-spezifische Budget-Übersicht */}
                {automaten.length > 0 && (
                  <div className="bg-white border rounded-lg overflow-hidden mb-8">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 bg-blue-50">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                        <i className="ri-robot-line mr-2 text-blue-600"></i>
                        Automaten Budget-Übersicht {new Date().getFullYear()}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">Monatliche Finanzaufschlüsselung pro Automat mit Umsatzbeteiligung und fixen Einnahmen</p>
                    </div>
                    
                    <div className="p-6 space-y-8">
                      {automaten.map(automat => (
                        <div key={automat.id} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                            <h4 className="text-sm sm:text-lg font-medium text-gray-900 flex items-center">
                              <i className="ri-robot-line mr-2 text-blue-600"></i>
                              {automat.name}
                              <span className="ml-2 text-xs sm:text-sm text-gray-500">
                                ({automat.location})
                              </span>
                            </h4>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monat</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Umsatz</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Wareneinsatz</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fixe Einnahmen</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fixkosten</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Umsatzbeteiligung</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Nettogewinn</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Marge</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {(() => {
                                  const currentYear = new Date().getFullYear();
                                  const automatResults = [];

                                  // Generate data for all 12 months for this automat
                                  for (let month = 1; month <= 12; month++) {
                                    const automatData = calculateAutomatMonthlyData(automat, currentYear, month);
                                    automatResults.push(automatData);
                                  }

                                  return automatResults.map((monthData, index) => (
                                    <tr key={monthData.month} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${monthData.isCurrentMonth ? 'bg-blue-50' : ''} ${monthData.isFutureMonth ? 'opacity-60' : ''}`}>
                                      <td className="px-4 py-2 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <div className={`w-2 h-2 rounded-full mr-2 ${monthData.isFutureMonth ? 'bg-gray-300' : monthData.netProfit > 0 ? 'bg-green-500' : monthData.netProfit < 0 ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                                          <div className="text-sm text-gray-900">
                                            {(() => {
                                              const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                                                'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                                              return `${monthNames[monthData.month - 1]} ${new Date().getFullYear()}`;
                                            })()}
                                            {monthData.isCurrentMonth && (
                                              <span className="ml-1 text-xs text-blue-600">●</span>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-gray-700">{formatCurrency(monthData.revenue)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.costs)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-green-600">+{formatCurrency(monthData.fixedRevenue)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.fixedCosts)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-orange-600">-{formatCurrency(monthData.revenueShare)}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-bold">
                                        <span className={monthData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                          {formatCurrency(monthData.netProfit)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                                        <span className={monthData.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                                          {monthData.netMargin.toFixed(1)}%
                                        </span>
                                      </td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
